import React from 'react';
import { Modal, Radio, message } from 'antd';
import { cancelRecurrencePrescriptionRefill } from 'api/textVisit';

type Props = {
    open: boolean;
    textVisitId?: number;
    patientName?: string;
    onClose: () => void;
    onSuccess?: (cancelAll: boolean) => void;
    // Optional override for the success toast (calendar uses its rich SuccessToast).
    showSuccessToast?: (msg: string) => void;
};

const CancelRecurringRefillModal: React.FC<Props> = ({
    open,
    textVisitId,
    patientName,
    onClose,
    onSuccess,
    showSuccessToast,
}) => {
    const [scope, setScope] = React.useState<'single' | 'future'>('single');
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (open) setScope('single');
    }, [open]);

    const handleConfirm = async () => {
        if (!textVisitId) {
            onClose();
            return;
        }
        const cancelAll = scope === 'future';
        setLoading(true);
        try {
            const res = await cancelRecurrencePrescriptionRefill({ textVisitId, cancelAll });
            if (res?.error) throw new Error('cancel failed');
            const successMsg = cancelAll
                ? `All future prescription refill tasks for ${(patientName || '').trim()} have been canceled successfully.`
                : 'Prescription refill task canceled successfully.';
            if (showSuccessToast) {
                showSuccessToast(successMsg);
            } else {
                message.success(successMsg);
            }
            onSuccess?.(cancelAll);
            onClose();
        } catch {
            message.error('Failed to cancel. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Cancel recurring task?"
            okText="Confirm"
            okButtonProps={{ loading }}
            cancelButtonProps={{ disabled: loading }}
            onOk={handleConfirm}
            onCancel={onClose}
            destroyOnClose
        >
            <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
                <Radio value="single" style={{ display: 'block', marginBottom: 12 }}>
                    Cancel this refill only
                </Radio>
                <Radio value="future" style={{ display: 'block' }}>
                    Cancel all future refills
                </Radio>
            </Radio.Group>
        </Modal>
    );
};

export default CancelRecurringRefillModal;
